import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Check,
  X,
} from "@phosphor-icons/react";
import { toast } from "sonner";


const EMPTY_FORM = {
  machine_id: "",
  issue_type: "breakdown",
  urgency: "normal",
  description: "",
};


const ISSUE_TYPES = [
  ["breakdown", "Panne"],
  ["blocked", "Machine bloquée"],
  ["quality", "Problème qualité"],
  ["noise", "Bruit anormal"],
  ["leak", "Fuite"],
  ["maintenance", "Maintenance / contrôle"],
  ["other", "Autre"],
];


const URGENCIES = [
  ["low", "Faible"],
  ["normal", "Normale"],
  ["high", "Haute"],
  ["urgent", "Urgente"],
];


function isInstalled(machine) {
  return (
    machine?.isArchived !== true &&
    (
      machine?.locationStatus === "installed" ||
      (
        !machine?.locationStatus &&
        Boolean(machine?.prospectId)
      )
    )
  );
}


function machineLabel(machine) {
  const name =
    machine.name ||
    machine.model ||
    "Machine";

  const details = [
    machine.serialNumber,
    machine.siteName,
  ]
    .filter(Boolean)
    .join(" · ");

  return details
    ? `${name} · ${details}`
    : name;
}


export default function SavTicketFormModal({
  open,
  machines = [],
  saving = false,
  onClose,
  onSubmit,
}) {
  const [form, setForm] = useState(
    EMPTY_FORM
  );


  useEffect(() => {
    if (open) {
      setForm({
        ...EMPTY_FORM,
      });
    }
  }, [open]);


  const availableMachines = useMemo(
    () =>
      machines
        .filter(isInstalled)
        .sort((left, right) =>
          machineLabel(left).localeCompare(
            machineLabel(right),
            "fr",
            {
              sensitivity: "base",
            }
          )
        ),
    [machines]
  );


  if (!open) {
    return null;
  }


  const updateField = (
    field,
    value
  ) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  };


  const submit = (event) => {
    event.preventDefault();

    if (!form.machine_id) {
      toast.error(
        "Sélectionnez une machine."
      );
      return;
    }

    const description =
      form.description.trim();

    if (!description) {
      toast.error(
        "Décrivez la demande SAV."
      );
      return;
    }

    onSubmit({
      machine_id: form.machine_id,
      issue_type: form.issue_type,
      urgency: form.urgency,
      description,
    });
  };


  const inputClass =
    "w-full rounded-lg border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2.5 text-sm outline-none transition focus:border-[var(--do-accent)] disabled:cursor-not-allowed disabled:opacity-60";


  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center acta-modal-overlay p-3 sm:p-6 backdrop-blur-sm"
      data-testid="sav-ticket-form-modal"
      onMouseDown={onClose}
    >
      <div
        className="acta-modal-surface w-full max-w-xl overflow-hidden rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] shadow-2xl"
        onMouseDown={(event) =>
          event.stopPropagation()
        }
      >
        <div className="flex items-center justify-between gap-4 border-b border-[var(--do-border)] px-4 py-4 sm:px-5">
          <div>
            <h2 className="font-heading text-lg font-black tracking-tight">
              Nouveau ticket
            </h2>

            <p className="mt-0.5 text-xs text-[var(--do-muted)]">
              Machine, problème et niveau d'urgence.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            aria-label="Fermer"
            className="rounded-lg p-2 transition hover:bg-[var(--do-surface-soft)] disabled:opacity-50"
          >
            <X size={20} />
          </button>
        </div>

        <form
          onSubmit={submit}
          className="space-y-4 p-4 sm:p-5"
        >
          <div>
            <label
              htmlFor="sav-machine"
              className="label-eyebrow mb-1.5 block"
            >
              Machine *
            </label>

            <select
              id="sav-machine"
              value={form.machine_id}
              onChange={(event) =>
                updateField(
                  "machine_id",
                  event.target.value
                )
              }
              disabled={
                saving ||
                availableMachines.length === 0
              }
              className={inputClass}
              autoFocus
            >
              <option value="">
                {availableMachines.length
                  ? "Sélectionner une machine"
                  : "Aucune machine installée disponible"}
              </option>

              {availableMachines.map(
                (machine) => (
                  <option
                    key={machine.id}
                    value={machine.id}
                  >
                    {machineLabel(machine)}
                  </option>
                )
              )}
            </select>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label
                htmlFor="sav-issue-type"
                className="label-eyebrow mb-1.5 block"
              >
                Problème *
              </label>

              <select
                id="sav-issue-type"
                value={form.issue_type}
                onChange={(event) =>
                  updateField(
                    "issue_type",
                    event.target.value
                  )
                }
                disabled={saving}
                className={inputClass}
              >
                {ISSUE_TYPES.map(
                  ([value, label]) => (
                    <option
                      key={value}
                      value={value}
                    >
                      {label}
                    </option>
                  )
                )}
              </select>
            </div>

            <div>
              <label
                htmlFor="sav-urgency"
                className="label-eyebrow mb-1.5 block"
              >
                Urgence *
              </label>

              <select
                id="sav-urgency"
                value={form.urgency}
                onChange={(event) =>
                  updateField(
                    "urgency",
                    event.target.value
                  )
                }
                disabled={saving}
                className={inputClass}
              >
                {URGENCIES.map(
                  ([value, label]) => (
                    <option
                      key={value}
                      value={value}
                    >
                      {label}
                    </option>
                  )
                )}
              </select>
            </div>
          </div>

          <div>
            <label
              htmlFor="sav-description"
              className="label-eyebrow mb-1.5 block"
            >
              Description *
            </label>

            <textarea
              id="sav-description"
              rows={4}
              maxLength={6000}
              value={form.description}
              onChange={(event) =>
                updateField(
                  "description",
                  event.target.value
                )
              }
              disabled={saving}
              placeholder="Décrivez rapidement le problème rencontré…"
              className={`${inputClass} resize-none`}
            />
          </div>

          <div className="flex justify-end gap-2 border-t border-[var(--do-border)] pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="rounded-lg border border-[var(--do-border)] px-4 py-2 text-sm font-heading font-semibold transition hover:bg-[var(--do-surface-soft)] disabled:opacity-50"
            >
              Annuler
            </button>

            <button
              type="submit"
              disabled={
                saving ||
                availableMachines.length === 0
              }
              className="inline-flex items-center gap-2 rounded-lg bg-[var(--do-accent)] px-4 py-2 text-sm font-heading font-bold text-white transition disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Check size={17} />
              {saving
                ? "Création…"
                : "Créer le ticket"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
